import { Component, OnInit, OnDestroy } from '@angular/core'
import { CommonModule } from '@angular/common'
import { MarriottAlarmRingingWidget, LogService, ProjectCMSWidgetType, SpecificActionSubType } from 'htv-lib'
import { BaseWidgetComponent } from '../base-widget/base-widget.component'
import { AlarmService } from '../../services/alarm.service'
import { AudioAlarmService } from '../../services/audio-alarm.service'
import { RouterService } from '../../services/router.service'
import { TranslateModule } from '@ngx-translate/core'
import { StateService } from '../../services/state.service'
import { WidgetActionService } from '../../services/widget-action.service'

const SNOOZE_MINUTES = 5

@Component({
  selector: 'app-marriott-alarm-ringing',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './marriott-alarm-ringing.component.html',
  styleUrls: ['./marriott-alarm-ringing.component.scss'],
})
export class MarriottAlarmRingingComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  currentTime = ''
  isPM = false

  constructor(
    private alarmService: AlarmService,
    private audioAlarmService: AudioAlarmService,
    private routerService: RouterService,
    private logService: LogService,
    private stateService: StateService,
    private widgetActionService: WidgetActionService
  ) {
    super()
  }

  override ngOnInit(): void {
    super.ngOnInit()
    this.loadCurrentTime()
    this.startAlarmSound()
    this.stateService.startShowingWidget(ProjectCMSWidgetType.MARRIOTT_ALARM_RINGING)
    this.registerWidgetActionListener()
    this.registerAlarmChangeListener()
  }

  private registerAlarmChangeListener(): void {
    this.subscriptions.add(
      this.alarmService.ringingAlarmChanged$.subscribe(newAlarm => {
        this.logService.debug(`Alarm changed to ${newAlarm.id} at ${newAlarm.time}`)
        this.loadCurrentTime()
      })
    )
  }

  registerWidgetActionListener(): void {
    this.subscriptions.add(
      this.widgetActionService.alarmRingingWidgetAction$.subscribe(action => {
        this.handleWidgetAction(action)
      })
    )
  }

  handleWidgetAction(action: SpecificActionSubType): void {
    switch (action) {
      case SpecificActionSubType.ALARM_STOP:
        this.stopAlarm()
        break
      case SpecificActionSubType.ALARM_SNOOZE:
        this.snoozeAlarm()
        break
    }
  }

  override ngOnDestroy(): void {
    this.audioAlarmService.stopAlarm()
    this.stateService.stopShowingWidget(ProjectCMSWidgetType.MARRIOTT_ALARM_RINGING)
    super.ngOnDestroy()
  }

  private startAlarmSound(): void {
    this.audioAlarmService.startAlarm().catch(error => {
      this.logService.error(`Could not start alarm sound: ${error}`)
    })
  }

  loadCurrentTime(): void {
    const savedTime = this.alarmService.lastTriggeredAlarmTime
    if (savedTime) {
      const [hours24, minutes] = savedTime.split(':').map(Number)

      let hours12: number
      if (hours24 === 0) {
        hours12 = 12
        this.isPM = false
      } else if (hours24 < 12) {
        hours12 = hours24
        this.isPM = false
      } else if (hours24 === 12) {
        hours12 = 12
        this.isPM = true
      } else {
        hours12 = hours24 - 12
        this.isPM = true
      }

      this.currentTime = `${String(hours12).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    } else {
      this.currentTime = '07:00'
      this.isPM = false
    }
  }

  stopAlarm(): void {
    this.audioAlarmService.stopAlarm()

    const alarm = this.alarmService.lastTriggeredAlarm
    if (!alarm?.id) {
      this.logService.error('No triggered alarm found to stop')
      this.alarmService.clearLastTriggeredAlarmTime()
      this.navigateBack()
      return
    }

    this.subscriptions.add(
      this.alarmService.deleteAlarm(alarm.id).subscribe({
        next: () => {
          this.logService.debug(`Alarm ${alarm.id} stopped and deleted successfully`)
          this.alarmService.clearLastTriggeredAlarmTime()
          this.navigateBack()
        },
        error: err => {
          this.logService.error(`Failed to delete alarm: ${err}`)
          this.alarmService.clearLastTriggeredAlarmTime()
          this.navigateBack()
        },
      })
    )
  }

  snoozeAlarm(): void {
    this.audioAlarmService.stopAlarm()

    const alarm = this.alarmService.lastTriggeredAlarm
    if (!alarm?.id) {
      this.logService.error('No triggered alarm found to snooze')
      this.alarmService.clearLastTriggeredAlarmTime()
      this.navigateBack()
      return
    }

    this.subscriptions.add(
      this.alarmService.snoozeAlarm(alarm.id).subscribe({
        next: () => {
          this.logService.debug(`Alarm ${alarm.id} snoozed for ${SNOOZE_MINUTES} minutes`)
          this.alarmService.clearLastTriggeredAlarmTime()
          this.navigateBack()
        },
        error: err => {
          this.logService.error(`Failed to snooze alarm: ${err}`)
          this.alarmService.clearLastTriggeredAlarmTime()
          this.navigateBack()
        },
      })
    )
  }

  private navigateBack(): void {
    this.routerService.goToHome()
  }

  override applyStyles(): void {
    super.applyStyles()

    const config = this.config as MarriottAlarmRingingWidget
    const headerStyle: Record<string, string | number> = {}
    const timeDisplayStyle: Record<string, string | number> = {}
    const timeStyle: Record<string, string | number> = {}
    const amPmStyle: Record<string, string | number> = {}
    const actionsStyle: Record<string, string | number> = {}
    const actionButtonStyle: Record<string, string | number> = {}
    const actionButtonSelectedStyle: Record<string, string | number> = {}

    if (config.backgroundColor) {
      headerStyle['background-color'] = config.backgroundColor
      timeDisplayStyle['background-color'] = config.backgroundColor
      actionsStyle['background-color'] = config.backgroundColor
    }

    if (config.textColor) {
      headerStyle['color'] = config.textColor
      timeStyle['color'] = config.textColor
      amPmStyle['color'] = config.textColor
      actionButtonStyle['color'] = config.textColor
      actionButtonStyle['border-color'] = config.textColor
    }

    if (config.buttonColor) {
      actionButtonSelectedStyle['border-color'] = config.buttonColor
      actionButtonSelectedStyle['background-color'] = config.buttonColor
    }

    this.setRule('.header', headerStyle)
    this.setRule('.time-display', timeDisplayStyle)
    this.setRule('.time', timeStyle)
    this.setRule('.am-pm', amPmStyle)
    this.setRule('.actions', actionsStyle)
    this.setRule('.action-button', actionButtonStyle)
    this.setRule('.action-button.htv-selected', actionButtonSelectedStyle)
  }
}
